import React from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import t from '../../utils/labels';
import { AuthRoutes } from '@food/auth/build/dist/components/AuthRoutes';
import { AuthContext } from '@food/auth/build/dist/components/AuthContext';
import { ScrollToTop } from '../ScrollToTop';
import { Route, Router, Switch } from 'react-router-dom';
import { history } from '../../utils/history';
import { ApolloProvider } from 'react-apollo';
import { cache, httpLink } from '../../utils/client';
import { LandingPageMagazineArgs } from '../../types';
import { UserRole } from '../../server-types';
import { authConfigDefault } from '../../utils/config';
import { RedirectHandler } from '@food/auth/build/dist/types';
import { auth } from '@food/auth/build/dist/logic/auth';
import { Channel } from '@food/sync';
import * as Sentry from '@sentry/browser';
import { stacked } from '../../utils/broadcastChannel';
import { ApolloLink } from 'apollo-link';
import { targetSiteLink } from '@food/auth/build/dist/links/targetSiteLink';
import { RenewLink } from '@food/auth/build/dist/links/RenewLink';
import ApolloClient from 'apollo-client';
import { checkForAchievements } from '../../utils/misc';
import { parse } from 'query-string';
import { AnalyticsListener } from '../AnalyticsListener';
import { LandingPageMagazineIndex } from '../entities/LandingPageMagazineIndex';
import { DefaultPageStyleClass } from '../pages/DefaultPage';
import { LayoutHandler } from '@food/ui';
import { DefaultLayout, defaultLayoutChooser } from '../../utils/layout';
import { cns, currySC } from '@food/css-manager';

export const authConfig = {
    ...authConfigDefault,
    AUTH_ROLES: [UserRole.Consumer, UserRole.Producer],
};

export const onRedirect: RedirectHandler = (_, redirectUrl) => {
    history.replace(redirectUrl);
};
export const authManager = auth(authConfig);
const authChannel = new Channel('@food/auth');

const userChangedHandler: (
    oldUser: {
        id: string;
        name: string;
        email: string;
    } | null,
    newUser: {
        id: string;
        name: string;
        email: string;
    } | null,
) => Promise<void> = async (
    _,
    newUser: {
        id: string;
        name: string;
        email: string;
    } | null,
) => {
        const { location } = history;
        const searchParams = parse(location.search);

        Sentry.configureScope((scope) => {
            if (newUser) {
                scope.setUser({
                    id: newUser.id,
                    username: newUser.name,
                    email: newUser.email,
                });
            } else {
                scope.setUser(null);
            }
        });

        //await client.clearStore();
        //await client.cache.reset();
        // client.reFetchObservableQueries();

        await client.cache.reset();
        client.resetStore();

        const redirectUrl = searchParams.redirect;
        if (redirectUrl) {
            onRedirect(newUser, redirectUrl as string);
        } else if (location.pathname === '/login') {
            onRedirect(newUser, '/');
        }
    };

authChannel.onmessage = stacked((events: any[]) => {
    const lastEvent = events.filter((e) => e.data.type === 'USER_CHANGED')[0];

    if (lastEvent) {
        const { oldUser, newUser } = lastEvent.data.args;
        userChangedHandler(oldUser, newUser);
    }
}, 100);

const link = ApolloLink.from([
    new (targetSiteLink(authConfig))(),
    new (RenewLink(authManager))(),
    httpLink,
]);

export const client = new ApolloClient({
    link,
    cache,
});

interface IProps extends WithTranslation {
    args: LandingPageMagazineArgs;
}

const LandingPageHandler = (args: any) => {
    const sc = currySC(DefaultPageStyleClass);
    return <LayoutHandler layouts={{ layout: defaultLayoutChooser }}>
        {({ layout }) => (
            <div className={cns(DefaultPageStyleClass, layout && sc('layout', layout))}>
                <div className={sc('main')}>
                    <LandingPageMagazineIndex {...args} layout={layout ? layout : DefaultLayout.DESKTOP} />
                </div>
            </div>
        )}
    </LayoutHandler>;
};

const authContainer: React.FC<any> = ({ children }) => <div>{children}</div>;

const InitialApp: React.FC<IProps> = (args) => {
    checkForAchievements();
    return (
        <Router history={history}>
            <ApolloProvider client={client}>
                <AnalyticsListener config={authConfig} authManager={authManager} />
                <AuthRoutes
                    config={authConfig}
                    authManager={authManager}
                    t={t}
                    onRedirect={onRedirect}
                    container={authContainer}
                >
                    <ScrollToTop>
                        <AuthContext>
                            <Switch>
                                <Route path={'/read'} exact render={(props) => <LandingPageHandler magazineId={args.args.magazineId} showIndex={args.args.showIndex} textColor={args.args.textColor} {...props} />} />
                                <Route path={'/'} exact render={(props) => <LandingPageHandler magazineId={args.args.magazineId} showIndex={args.args.showIndex} textColor={args.args.textColor}  {...props} />} />
                            </Switch>

                        </AuthContext>
                    </ScrollToTop>
                </AuthRoutes>
            </ApolloProvider>
        </Router>
    );
};

const LandingPageMagazine = withTranslation('translations')(InitialApp);

export default LandingPageMagazine;
